import { IconPlus } from '@developerz/ui';
import { createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { TaskRow } from '../lib/fleet';
import type { PagedList } from '../lib/paged';
import { createPagination } from '../lib/paginate';
import { createTaskStop, type TaskStopController } from '../lib/taskStop';
import { createToast } from '../lib/toast';
import { CardGridSkeleton } from './CardGridSkeleton';
import { EmptyState } from './EmptyState';
import { FleetQueueBand } from './FleetQueueBand';
import { LoadMore } from './LoadMore';
import { NewTaskModal } from './NewTaskModal';
import { Pager } from './Pager';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { StopTaskModal } from './StopTaskModal';
import { TaskDetailDrawer } from './TaskDetailDrawer';
import { TaskQueueItem } from './TaskQueueItem';
import { Toast } from './Toast';
import { Button } from './ui/Button';

// The task queue board (#513) — presenter over the paged `GET /v1/tasks` read the
// container hands down (`useTaskQueue`, live on the `tasks` topic: a change re-reads,
// #7612). This owns how the board renders: the running-vs-queued count band, the
// rows, the loading/empty branches, and the button that pulls the next cursor
// page. `list.view` reports loading/error only while
// nothing is on screen, so a load-more never flashes a skeleton over the rows
// already read.
//
// THE ROWS ARE PAGED IN MEMORY, and that is a different problem from the
// cursor. A busy account's board ran to a hundred full-height rows in one
// unbounded list — the panel grew until the page was nothing else. `<LoadMore/>`
// is not the answer to that (it fetches MORE), so the rows already held are
// walked with `createPagination` + `<Pager/>`, the Home-panel primitive, which
// re-derives as a re-read lands. The count band above still counts
// EVERY row, never the visible page: it answers "how much work is in flight",
// which is not a question about scroll position.
//
// It also owns the board's two write/read surfaces (#1034): "New task"
// (`POST /v1/tasks`) and the per-row detail drawer (`GET /v1/tasks/:id`). Only
// which one is open lives here — each fetches its own data — so the route stays
// a container over the queue and this stays the one place that knows a board row
// can be opened. After a dispatch the list is `reload()`ed: the POST's echo is a
// verdict, never a row, and page one sits inside the 30s staleTime it invalidated.

export type FleetQueuePanelProps = {
  /** The paged queue read — its rows, the boundary's gates and Load more. */
  readonly list: PagedList<TaskRow>;
  /** Session bearer for the dispatch + detail reads (injectable for tests). */
  readonly token: string | null;
  /**
   * Narrow the board to one machine's work. `id` is the machine's `runner_id` —
   * the only machine identity a queue row carries — and it is ALSO what the URL
   * holds (`?machine=`), so the filter survives a reload and a shared link.
   * `name` is the machine's display name, supplied by the caller (the presenter
   * reads no machines); absent, the chip shows the bare id. The rows are
   * ALREADY narrowed by the server (`GET /v1/tasks?filter=runner_id:eq:<id>`,
   * #6788): this panel never filters, because a client filter over the rows
   * held silently drops a machine's older work. Here `machine` only names the narrow and offers the way out.
   */
  readonly machine?: { readonly id: string; readonly name?: string } | null;
  /** Drop the narrow. Required whenever `machine` is set — a filter with no way out is a trap. */
  readonly onClearMachine?: () => void;
  /**
   * Which task's drawer is open, and who opens it. Absent the panel owns both
   * (the /fleet page's board); a page that also renders rows ABOVE this one
   * (routes/FleetWork's summary band) passes its own, so a summary row's Details
   * opens THIS drawer instead of a second component owning the same task.
   */
  readonly openTask?: () => string | null;
  readonly onOpenTask?: (taskId: string | null) => void;
  /**
   * The page's stop controller, when rows above this panel can ask too (the
   * FleetWork summary band). Absent the panel mints its own — the /fleet board's
   * standing behaviour. Passing one means ONE `StopTaskModal` for a task id
   * across both bands, which is the point: two confirms for one row is a
   * destructive action the reader can start twice from the same page.
   */
  readonly stop?: TaskStopController;
  /**
   * The caller may write tasks (`write:tasks`, `canWriteTasks`): New task and Stop.
   * A viewer sees neither (owner ruling G12, #6788); the server would 403 both.
   * Absent = yes, the roles.ts rule for an unknown role.
   */
  readonly canWrite?: boolean;
};

/** Rows per board page. Task rows are two-line cards, so a screenful is ~10. */
const QUEUE_PAGE_SIZE = 10;

export function FleetQueuePanel(props: FleetQueuePanelProps): JSX.Element {
  const [dispatching, setDispatching] = createSignal(false);
  const [ownOpenTask, setOwnOpenTask] = createSignal<string | null>(null);
  // One owner, whichever side asked: a page with rows above the board hands both
  // halves down, so there is exactly ONE `TaskDetailDrawer` for a task id.
  const openTask = (): string | null => (props.openTask ?? ownOpenTask)();
  const setOpenTask = (taskId: string | null): void => {
    if (props.onOpenTask) props.onOpenTask(taskId);
    else setOwnOpenTask(taskId);
  };
  const { toast, notify, dismiss } = createToast();
  const machineId = (): string | null => props.machine?.id ?? null;
  const canWrite = (): boolean => props.canWrite ?? true;
  // The board is LIVE (`tasks` topic), so a stopped row repaints from the re-read
  // its own change triggers — no reload, which would throw away the accumulated
  // pages the reader is sitting on. The toast is the only other signal that it landed.
  const ownStop = createTaskStop({
    token: props.token,
    onStopped: ({ alreadyTerminal }) =>
      notify(
        alreadyTerminal ? t('fleet.stop.already_terminal_toast') : t('fleet.stop.stopped_toast'),
      ),
  });
  // A page that shares this controller already shows its own toast for a stop
  // that landed, so only the panel's OWN stops toast here.
  const stop: TaskStopController = props.stop ?? ownStop;
  const stopToast = (): string | null => (props.stop === undefined ? ownStop.error() : null);
  const rows = (): readonly TaskRow[] => props.list.rows();
  const paged = createPagination(rows, QUEUE_PAGE_SIZE);
  // The narrow's chip and its way out. Rendered with the board AND with the
  // empty state: a `?machine=` link whose machine has no rows must still offer
  // the clear control — an empty branch without it is the trap line 75 refuses.
  const machineChip = (): JSX.Element => (
    <Show when={machineId()}>
      {(id) => (
        <p class="panel__footer">
          {t('fleet.queue_machine_filter', {
            machine: props.machine?.name ?? id(),
          })}
          <Button variant="ghost" size="sm" onClick={() => props.onClearMachine?.()}>
            {t('fleet.queue_machine_clear')}
          </Button>
        </p>
      )}
    </Show>
  );

  return (
    <Panel
      title={t('fleet.queue_heading')}
      action={
        <Show when={canWrite()}>
          <Button size="sm" onClick={() => setDispatching(true)}>
            <IconPlus />
            {t('fleet.new_task.open')}
          </Button>
        </Show>
      }
    >
      <QueryBoundary
        query={props.list.view}
        // Empty-state keys off every page held, not the last one read.
        isEmpty={() => rows().length === 0}
        loading={<CardGridSkeleton listClass="grid-cards-3" />}
        empty={
          <>
            <EmptyState
              message={
                machineId() === null ? t('fleet.queue_empty') : t('fleet.queue_machine_empty')
              }
              {...(canWrite()
                ? { ctaLabel: t('fleet.new_task.open'), onCta: () => setDispatching(true) }
                : {})}
            />
            {machineChip()}
          </>
        }
      >
        {() => (
          <>
            {machineChip()}
            <FleetQueueBand rows={rows()} />
            <ul class="fleet-queue">
              <For each={paged.slice()}>
                {(task) => (
                  <TaskQueueItem
                    task={task}
                    onOpen={setOpenTask}
                    onStop={canWrite() ? stop.ask : undefined}
                  />
                )}
              </For>
            </ul>
            <Pager pagination={paged} label={t('fleet.queue_heading')} />
            <LoadMore list={props.list} />
          </>
        )}
      </QueryBoundary>

      <NewTaskModal
        open={dispatching()}
        token={props.token}
        onClose={() => setDispatching(false)}
        onDispatched={() => props.list.reload()}
        onOpenTask={setOpenTask}
      />
      <TaskDetailDrawer taskId={openTask()} token={props.token} onClose={() => setOpenTask(null)} />
      {/* ONE confirm for one page. When the page hands down its own controller
          (`props.stop`, routes/FleetWork) IT renders the modal — this one stays
          unmounted, because two mounted modals over one controller put TWO
          dialogs on screen for a single destructive action, each able to confirm
          the same ask. */}
      <Show when={props.stop === undefined}>
        <StopTaskModal task={stop.target()} onConfirm={stop.confirm} onClose={stop.close} />
      </Show>
      <Toast message={stopToast()} tone="error" onDismiss={stop.clearError} />
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
    </Panel>
  );
}
